/**
 * Shared template for the semi-programmatic keyword landing pages
 * (`/wg-zimmer-muenchen`, …). One component renders every row in
 * `KEYWORD_PAGES`; `scripts/prerender.ts` bakes the same meta + FAQPage JSON-LD
 * into the shipped HTML so crawlers and LLM bots see real content.
 *
 * Content only — Navigation/Footer come from the marketing layout in App.tsx,
 * outside <Routes> (same as Careers.tsx). Copy is German-first from the config
 * row; EN counterparts are a documented follow-up (add rows with lang:"en").
 */
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Seo } from "@/components/Seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  keywordPage,
  keywordPageMeta,
  keywordPageRoomsHref,
} from "@/lib/seo/config";
import { INK, INK_DEEP, MUTED, SAND, LINE_STRONG } from "@/lib/marketing-theme";

const H1 =
  "font-display text-[2.5rem] font-semibold leading-[1.04] sm:text-[3.25rem]";

export default function KeywordLanding({ slug }: { slug: string }) {
  const page = keywordPage(slug);
  // Unreachable in practice — App.tsx only mounts this for configured slugs —
  // but keeps the component total and lets prerender stay the single source.
  if (!page) return null;

  return (
    <div className="min-h-screen bg-white">
      <Seo meta={keywordPageMeta(page)} />

      {/* ===== Hero ===== */}
      <section
        className="px-5 sm:px-8 lg:px-14"
        style={{ background: INK_DEEP, paddingTop: "var(--hero-offset)" }}
      >
        <div className="mx-auto flex max-w-[1328px] flex-col gap-8 py-20 lg:py-28">
          <h1 className={H1} style={{ letterSpacing: "-0.04em", color: SAND }}>
            {page.h1}
          </h1>
          <div className="flex max-w-[62ch] flex-col gap-4">
            {page.intro.map((p) => (
              <p
                key={p}
                className="text-[17px] leading-[1.6]"
                style={{ color: "rgba(250,248,244,0.82)" }}
              >
                {p}
              </p>
            ))}
          </div>
          <Link
            to={keywordPageRoomsHref(page)}
            className="inline-flex h-[54px] w-fit items-center gap-2 rounded-full px-7 text-[15.5px] font-semibold transition-opacity hover:opacity-90"
            style={{ background: SAND, color: INK }}
          >
            {page.ctaLabel}
            <ArrowRight className="h-4 w-4" strokeWidth={1.9} />
          </Link>
        </div>
      </section>

      {/* ===== Benefits ===== */}
      <section className="px-5 sm:px-8 lg:px-14">
        <div className="mx-auto max-w-[1328px] py-16 lg:py-20">
          <ul className="grid gap-x-12 gap-y-5 sm:grid-cols-2">
            {page.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <Check
                  className="mt-1 h-5 w-5 shrink-0"
                  strokeWidth={2.2}
                  style={{ color: INK }}
                />
                <span
                  className="text-[16.5px] leading-[1.55]"
                  style={{ color: INK }}
                >
                  {b}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== FAQ (also emitted as FAQPage JSON-LD) ===== */}
      <section className="px-5 sm:px-8 lg:px-14" style={{ background: SAND }}>
        <div className="mx-auto max-w-[760px] py-16 lg:py-20">
          <h2
            className="font-display text-[2rem] font-semibold leading-[1.06] sm:text-[2.5rem]"
            style={{ letterSpacing: "-0.04em", color: INK }}
          >
            Häufige Fragen
          </h2>
          <Accordion type="single" collapsible className="mt-8">
            {page.faq.map((item, i) => (
              <AccordionItem
                key={item.question}
                value={`faq-${i}`}
                style={{ borderColor: LINE_STRONG }}
              >
                <AccordionTrigger
                  className="text-left text-[17px]"
                  style={{ color: INK }}
                >
                  {item.question}
                </AccordionTrigger>
                <AccordionContent
                  className="text-[15.5px] leading-[1.6]"
                  style={{ color: MUTED }}
                >
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <Link
            to={keywordPageRoomsHref(page)}
            className="mt-12 inline-flex h-[54px] w-fit items-center gap-2 rounded-full px-7 text-[15.5px] font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: INK }}
          >
            {page.ctaLabel}
            <ArrowRight className="h-4 w-4" strokeWidth={1.9} />
          </Link>
        </div>
      </section>
    </div>
  );
}
